import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { gameAPI } from '../../services/gameAPI';
import { useGameStore } from '../../store/gameStore';
import {
  Loader2,
  BookOpen,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Tutorial-specific case summary modal
 * Completely separate from main game - no isTutorial conditionals
 */
export const TutorialSummaryModal = ({ open, onClose, sessionId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const selectedTheme = useGameStore((state) => state.selectedTheme);
  const resetGame = useGameStore((state) => state.resetGame);
  const navigate = useNavigate();

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Tutorial-specific endpoint - no session required
      const data = await gameAPI.getTutorialSummary(sessionId);
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch tutorial summary:', err);
      setError('Failed to load tutorial summary');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && sessionId) {
      fetchSummary();
    }
  }, [open, sessionId, fetchSummary]);

  const handleNewGame = () => {
    resetGame();
    onClose();
    navigate('/');
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-4xl lg:max-w-5xl bg-slate-900 border-white/20">
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
            <p className="text-gray-400">Loading tutorial summary...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !summary) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-md bg-slate-900 border-white/20">
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-white font-semibold mb-2">Unable to Load Summary</p>
            <p className="text-gray-400 text-sm">{error || 'Something went wrong'}</p>
            <Button onClick={onClose} className="mt-4" variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Tutorial modals cannot be dismissed by clicking outside - must use button
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-4xl lg:max-w-5xl max-h-[90vh] bg-slate-900 border-2"
        style={{ borderColor: themeAccent }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton={true}
      >
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-white text-center">
            Tutorial Complete!
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="truth" className="flex-1">
          <TabsList className="grid grid-cols-3 bg-slate-800">
            <TabsTrigger value="truth" className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">The Truth</span>
            </TabsTrigger>
            <TabsTrigger value="missed" className="flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Clues</span>
            </TabsTrigger>
            <TabsTrigger value="fates" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Fates</span>
            </TabsTrigger>
          </TabsList>

          {/* The Truth Tab */}
          <TabsContent value="truth" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-4">
                {/* Killer Reveal */}
                <div
                  className="bg-slate-800 rounded-lg p-4 border-l-4"
                  style={{ borderColor: themeAccent }}
                >
                  <h3 className="text-xl font-bold text-white mb-2">
                    The Killer: {summary.killer.name}
                  </h3>
                  <p className="text-gray-400">
                    {summary.killer.occupation}
                  </p>
                  <p className="text-gray-300 mt-2">
                    <strong>Motive:</strong> {summary.killer.motive}
                  </p>
                </div>

                {/* Narrative */}
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-200 leading-relaxed whitespace-pre-line">
                    {summary.narrative?.theTruth}
                  </p>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Clues Discovery Tab */}
          <TabsContent value="missed" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-4">
                {/* Discovery Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h4 className="text-sm text-gray-400 mb-1">Clues Found</h4>
                    <p className="text-2xl font-bold text-white">
                      {summary.discoveryStats.clues.found} / {summary.discoveryStats.clues.total}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {summary.discoveryStats.clues.percentage}%
                    </p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h4 className="text-sm text-gray-400 mb-1">Suspects Interviewed</h4>
                    <p className="text-2xl font-bold text-white">
                      {summary.discoveryStats.suspects.found} / {summary.discoveryStats.suspects.total}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {summary.discoveryStats.suspects.percentage}%
                    </p>
                  </div>
                </div>

                {/* Missed Clues */}
                {(summary?.missedClues?.length ?? 0) === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-green-400 font-semibold">
                      Perfect Investigation!
                    </p>
                    <p className="text-gray-400">
                      You found every clue in the tutorial.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-gray-400 mb-4">
                      You missed {summary?.missedClues?.length ?? 0} clue(s):
                    </p>
                    {(summary?.missedClues ?? []).map((clue, index) => (
                      <div
                        key={index}
                        className="bg-slate-800 rounded-lg p-4 border-l-4 border-yellow-500"
                      >
                        <h4 className="text-white font-semibold flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-yellow-500" />
                          {clue.name}
                        </h4>
                        <p className="text-gray-400 text-sm mt-1">
                          {clue.description}
                        </p>
                        {clue.location && (
                          <p className="text-gray-500 text-xs mt-2">
                            Location: {clue.location}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Character Fates Tab */}
          <TabsContent value="fates" className="mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              {summary.narrative?.characterFates?.length > 0 ? (
                <div className="space-y-3">
                  {summary.narrative.characterFates.map((char, index) => (
                    <div
                      key={index}
                      className="bg-slate-800 rounded-lg p-4"
                    >
                      <h4 className="text-white font-semibold mb-1">
                        {char.name}
                      </h4>
                      <p className="text-gray-400 text-sm">
                        {char.fate}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  Character fates not available.
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
          <Button
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
            onClick={handleNewGame}
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <Button
            onClick={onClose}
            className="flex-1 font-semibold ring-4 ring-amber-400 animate-pulse"
            style={{ backgroundColor: themeAccent }}
            data-tutorial="close-summary"
          >
            Close Tutorial
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
